import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import spotlightService from '../services/SpotlightService';
import { cacheService } from '../services/CacheService';
import episodeSelectionService from '../services/EpisodeSelectionService';
import analytics from '../services/analytics';
import userDataService from '../services/UserDataService';
import cmsService, { extractGigcasterIdFromChannel } from '../services/CMSService';
import { getChannelEventDateValue, formatChannelEventDate } from '../utils/channelDateUtils';
import { getImageCdnBase, FALLBACK_IMAGE_SRC } from '../utils/ImageUtils';
import PDPModal from './PDPModal';
import ProgressBar from './common/ProgressBar';
import LiveBadge from './LiveBadge';
import { useLiveEventStatus } from '../hooks/useLiveEventStatus';
import './ChannelCard.css';
import { getPosterSize } from '../config/categoryRailSettings';

const ChannelCard = React.memo(
  ({
    channel = {},
    style,
    index,
    onHoverModalShow,
    onHoverModalHide,
    onCardClick,
    externalClickHandle,
  }) => {
    const safeChannel = channel || {};

    // LIVE badge — gated useLiveEventStatus only calls the backend for near/live
    // events (slim 1h window), so far-future cards make no request.
    const liveGigcasterId =
      extractGigcasterIdFromChannel(safeChannel) ||
      cmsService.getCachedGigcasterIdForSlug?.(safeChannel.slug || safeChannel.strSlug) ||
      null;
    const { isLive } = useLiveEventStatus(liveGigcasterId);

    const isTopNumber = style === 'top-number' || style === 'top-number-poster';
    const isPosterFormat = style === 'top-number-poster' || style === 'poster';
    const isContinueWatching = style === 'continue-watching';

    // `spotlight_poster` (CMS "Key Art") is reserved as the 2:3 portrait asset, so
    // it is only ever a fallback here. `poster` leads for both frames; see ResultCard.
    const originalImageUrl =
      safeChannel.poster ||
      safeChannel.spotlight_poster ||
      (safeChannel.image && (safeChannel.image.poster || safeChannel.image.spotlight_poster));

    let imageUrl = null;
    if (originalImageUrl) {
      const sizeSuffix = isPosterFormat ? '320/480?webp=1' : '480/270?webp=1';
      if (originalImageUrl.startsWith('http')) {
        // Keep the API's own host and path; only append the existing sizing grammar.
        const url = new URL(originalImageUrl);
        const basePath = `${url.origin}${url.pathname.replace(/\/$/, '')}`;
        imageUrl = `${basePath}/${sizeSuffix}`;
      } else {
        // Bare id: the API-supplied image CDN base (from /init/config) is the
        // only source. With no base there is no URL to build — fall through to
        // the placeholder rather than emit a relative /images/<id>.
        const imagesBase = getImageCdnBase();
        imageUrl = imagesBase ? `${imagesBase}/${originalImageUrl}/${sizeSuffix}` : null;
      }
    }
    if (!imageUrl) {
      imageUrl = FALLBACK_IMAGE_SRC;
    }

    const title = safeChannel.title || safeChannel.name || 'Untitled Channel';

    const [imageError, setImageError] = useState(false);
    const [showPDPModal, setShowPDPModal] = useState(false);
    const [pdpVideoState, setPdpVideoState] = useState({ autoPlay: false, currentTime: 0 });
    const [progressData, setProgressData] = useState(null);
    const [progressLoading, setProgressLoading] = useState(false);
    const [resolvedGigcasterId, setResolvedGigcasterId] = useState(() =>
      extractGigcasterIdFromChannel(safeChannel),
    );

    const cardRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const slimEventData = resolvedGigcasterId
      ? cmsService.getSlimEventData(resolvedGigcasterId)
      : null;
    const eventDateLabel = formatChannelEventDate(
      getChannelEventDateValue(safeChannel, slimEventData),
    );

    const handleImageError = () => {
      // Guard against a loop: once we've already swapped to the fallback,
      // ignore further errors (the fallback is a data URI and can't itself
      // fail to load, but this keeps a bad edit from re-triggering forever).
      if (imageError) {
        return;
      }
      console.warn(`Failed to load image: ${imageUrl}`);
      setImageError(true);
    };

    const isSeries =
      safeChannel.programming_type === 'Series' ||
      safeChannel.type === 'series' ||
      safeChannel.content_type === 'series';

    const channelSlug =
      safeChannel.slug || safeChannel.permalink || safeChannel._id || safeChannel.id;

    const handleClick = () => {
      analytics.trackSelectContent({
        id: safeChannel._id || safeChannel.id,
        title: safeChannel.title || safeChannel.name,
        type: safeChannel.programming_type || safeChannel.content_type,
      });

      if (externalClickHandle) {
        return externalClickHandle();
      }

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (onCardClick) onCardClick();
      setShowPDPModal(true);
    };

    const { isAuthenticated } = useAuthContext();

    useEffect(() => {
      let isMounted = true;

      const resolveGigcasterId = async () => {
        const directId = extractGigcasterIdFromChannel(safeChannel);
        if (directId) {
          setResolvedGigcasterId(directId);
          return;
        }

        const resolvedIds = await cmsService.resolveGigcasterIdsForChannel(safeChannel);
        if (isMounted) {
          setResolvedGigcasterId(resolvedIds[0] || null);
        }
      };

      resolveGigcasterId();

      return () => {
        isMounted = false;
      };
    }, [safeChannel._id, safeChannel.slug, safeChannel.permalink]);

    useEffect(() => {
      const fetchProgressData = async () => {
        if (!isAuthenticated || !isSeries || !safeChannel._id || !isContinueWatching) {
          setProgressData(null);
          return;
        }

        setProgressLoading(true);

        try {
          const latestResumption = userDataService.getLatestResumption(safeChannel._id);
          if (!latestResumption) {
            setProgressData(null);
            return;
          }

          setProgressData({
            title: latestResumption.episode.title,
            episodeNumber: latestResumption.episode.episode,
            seasonNumber: latestResumption.episode.channel?.season || 1,
            progress: latestResumption.progress,
            point: latestResumption.point,
            duration: latestResumption.duration,
            isSeries: true,
          });
        } catch (error) {
          console.error('ChannelCard: Error fetching progress data:', error);
          setProgressData(null);
        } finally {
          setProgressLoading(false);
        }
      };

      fetchProgressData();
    }, [isAuthenticated, isSeries, isContinueWatching, safeChannel._id]);

    const MODAL_POSITIONING = {
      offsetX: -20,
      offsetY: 0,
      styleOffsets: {
        'top-number-poster': { offsetX: -100, offsetY: -10 },
        'top-number': { offsetX: -15, offsetY: 5 },
        default: { offsetX: -40, offsetY: -20 },
      },
      responsiveOffsets: {
        mobile: { offsetX: -10, offsetY: 0 },
        tablet: { offsetX: -15, offsetY: 0 },
        desktop: { offsetX: -20, offsetY: 0 },
      },
      anchor: { horizontal: 'left', vertical: 'top' },
      boundaryHandling: { enabled: true, padding: 20, fallbackToCenter: false },
    };

    const calculateModalPosition = cardElement => {
      if (!cardElement) {
        return { x: 0, y: 0 };
      }

      const rect = cardElement.getBoundingClientRect();
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const currentStyle = style || 'default';

      let responsiveOffset = MODAL_POSITIONING.responsiveOffsets.desktop;
      if (viewport.width <= 768) responsiveOffset = MODAL_POSITIONING.responsiveOffsets.mobile;
      else if (viewport.width <= 1024)
        responsiveOffset = MODAL_POSITIONING.responsiveOffsets.tablet;

      const styleOffset =
        MODAL_POSITIONING.styleOffsets[currentStyle] || MODAL_POSITIONING.styleOffsets.default;

      let baseX = rect.left;
      let baseY = rect.top;

      const finalOffsetX =
        styleOffset.offsetX ?? responsiveOffset.offsetX ?? MODAL_POSITIONING.offsetX;
      const finalOffsetY =
        styleOffset.offsetY ?? responsiveOffset.offsetY ?? MODAL_POSITIONING.offsetY;

      let finalX = baseX + finalOffsetX;
      let finalY = baseY + finalOffsetY;

      if (MODAL_POSITIONING.boundaryHandling.enabled) {
        const modalWidth = 416;
        const modalHeight = 400;
        const padding = MODAL_POSITIONING.boundaryHandling.padding;

        if (finalX < padding) finalX = padding;
        else if (finalX + modalWidth > viewport.width - padding)
          finalX = viewport.width - modalWidth - padding;

        if (finalY < padding) finalY = padding;
        else if (finalY + modalHeight > viewport.height - padding)
          finalY = viewport.height - modalHeight - padding;
      }

      return { x: finalX, y: finalY };
    };

    const handleHoverModalShow = useCallback(() => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        if (cardRef.current && onHoverModalShow) {
          const position = calculateModalPosition(cardRef.current);
          onHoverModalShow(safeChannel, position, index);
        }
      }, 600);
    }, [safeChannel, onHoverModalShow, index]);

    const handleHoverModalHide = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (onHoverModalHide) onHoverModalHide();
    }, [onHoverModalHide]);

    const { handleMouseEnter: handlePrefetchEnter, handleMouseLeave: handlePrefetchLeave } =
      useHoverPrefetch(channelSlug, isSeries ? 'series' : 'channel', 500);

    const handleMouseEnter = useCallback(() => {
      handlePrefetchEnter();
      handleHoverModalShow();
    }, [handlePrefetchEnter, handleHoverModalShow]);

    const handleMouseLeave = useCallback(() => {
      handlePrefetchLeave();
      handleHoverModalHide();
    }, [handlePrefetchLeave, handleHoverModalHide]);

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      };
    }, []);

    const hasRecentlyAddedLabel = safeChannel.custom_fields?.card_label === 'recently_added';
    const shouldShowLock = safeChannel.subscription_access?.unlocked === false;
    const posterSize = isPosterFormat || isTopNumber ? getPosterSize(window.innerWidth) : null;

    return (
      <>
        <div
          ref={cardRef}
          className={`w-full min-w-0 cursor-pointer channel-card ${isTopNumber ? 'top-number-card' : ''}`}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="relative overflow-hidden rounded-lg">
            {isLive && (
              <div className="absolute z-20 top-2 left-2">
                <LiveBadge />
              </div>
            )}

            {isTopNumber && (
              <div className="absolute top-0 left-0 z-10 number-overlay">
                <span className="number-text">{index + 1}</span>
              </div>
            )}

            {hasRecentlyAddedLabel && (
              <div className="absolute z-20 px-2 py-1 text-xs font-semibold rounded-md shadow-lg top-2 right-2 bg-accent text-primary">
                Recently Added
              </div>
            )}

            {shouldShowLock && (
              <div className="absolute z-20 top-2 left-2 bg-accent rounded-full p-1.5 shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="white"
                  className="w-4 h-4"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}

            <img
              src={imageError ? FALLBACK_IMAGE_SRC : imageUrl}
              alt={title}
              style={
                isTopNumber ? { width: posterSize?.width, height: posterSize?.height } : undefined
              }
              className={`object-cover w-full transition-transform duration-300 hover:scale-105 ${
                isPosterFormat ? 'aspect-[2/3]' : 'aspect-video'
              }`}
              onError={handleImageError}
              loading="lazy"
            />

            {/* Progress Overlay for Continue Watching */}
            {isContinueWatching && (
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                {(safeChannel.resumption_data ||
                  (isSeries && progressData && !progressLoading)) && (
                  <ProgressBar
                    progressData={
                      safeChannel.resumption_data
                        ? {
                            title: safeChannel.parent_channel_title
                              ? safeChannel.episode_title
                              : safeChannel.title,
                            episodeNumber: (() => {
                              if (safeChannel.parent_channel_id) {
                                try {
                                  const seriesId = safeChannel.parent_channel_id;
                                  const continueWatchingInfo =
                                    episodeSelectionService.getContinueWatchingEpisode(
                                      seriesId,
                                      safeChannel.childchannels,
                                    );
                                  if (continueWatchingInfo?.episode) {
                                    return (
                                      continueWatchingInfo.episode.episode_number ||
                                      continueWatchingInfo.episode.episode ||
                                      1
                                    );
                                  }
                                } catch (e) {
                                  /* fallback below */
                                }
                              }
                              return (
                                safeChannel.resumption_data.episodeNumber ||
                                safeChannel.resumption_data.channel?.episode
                              );
                            })(),
                            seasonNumber: (() => {
                              if (safeChannel.parent_channel_id) {
                                try {
                                  const seriesId = safeChannel.parent_channel_id;
                                  const continueWatchingInfo =
                                    episodeSelectionService.getContinueWatchingEpisode(
                                      seriesId,
                                      [],
                                    );
                                  if (continueWatchingInfo?.season) {
                                    return (
                                      continueWatchingInfo.season.season_number ||
                                      continueWatchingInfo.season.season ||
                                      1
                                    );
                                  }
                                } catch (e) {
                                  /* fallback below */
                                }
                              }
                              return (
                                safeChannel.resumption_data.seasonNumber ||
                                safeChannel.resumption_data.channel?.season ||
                                1
                              );
                            })(),
                            progress: safeChannel.resumption_data.progress,
                            point: safeChannel.resumption_data.point,
                            duration: safeChannel.resumption_data.duration,
                            isSeries: !!safeChannel.parent_channel_id,
                            showResumeText: true,
                          }
                        : progressData
                    }
                    variant="card"
                    size="small"
                    showProgressBar={true}
                    showEpisodeInfo={!!safeChannel.parent_channel_id || isSeries}
                    className="text-white"
                  />
                )}
              </div>
            )}
          </div>
          <div className="channel-card-meta">
            <h3 className="channel-card-title">{title}</h3>
            {eventDateLabel && <p className="channel-card-date">{eventDateLabel}</p>}
          </div>
        </div>

        {showPDPModal && (
          <PDPModal
            channel={safeChannel}
            isVisible={showPDPModal}
            onClose={() => setShowPDPModal(false)}
            autoPlayVideo={pdpVideoState.autoPlay}
            videoCurrentTime={pdpVideoState.currentTime}
          />
        )}
      </>
    );
  },
);

export default ChannelCard;

const useHoverPrefetch = (channelSlug, type, delay = 300) => {
  const timeoutRef = useRef(null);
  const hasPrefetched = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (hasPrefetched.current || !channelSlug) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        const cacheKey = `channel_${channelSlug}`;
        if (cacheService.get(cacheKey)) return;
        await spotlightService.getChannelBySlug(channelSlug).catch(e => {
          throw new Error(e);
        });
        hasPrefetched.current = true;
      } catch (error) {
        console.warn(`Prefetch failed for channel ${channelSlug}:`, error);
      }
    }, delay);
  }, [channelSlug, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { handleMouseEnter, handleMouseLeave };
};
